import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { User, Bell, Shield, Key, Users, Building2, CreditCard } from "lucide-react";

export function SettingsPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Settings</h2>
        <p className="text-muted-foreground">
          Manage admin account and platform settings
        </p>
      </div>

      {/* Admin Profile */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <User className="h-5 w-5" />
            <CardTitle>Admin Profile</CardTitle>
          </div>
          <CardDescription>
            Your administrator account information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src="" />
              <AvatarFallback className="text-lg bg-primary text-primary-foreground">AD</AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <p className="text-sm font-medium">Profile Picture</p>
              <p className="text-xs text-muted-foreground">
                JPG, GIF or PNG. Max size 2MB.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  Upload
                </Button>
                <Button variant="ghost" size="sm">
                  Remove
                </Button>
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="adminFirstName">First Name</Label>
              <Input id="adminFirstName" placeholder="Admin" defaultValue="Admin" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adminLastName">Last Name</Label>
              <Input id="adminLastName" placeholder="User" defaultValue="User" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="adminEmail">Email</Label>
              <Input
                id="adminEmail"
                type="email"
                placeholder="admin@dovie.app"
                defaultValue="admin@dovie.app"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button>Save Changes</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Platform Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              <CardTitle>Platform Settings</CardTitle>
            </div>
            <CardDescription>
              Configure your platform branding
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="platformName">Platform Name</Label>
              <Input id="platformName" defaultValue="Dovie" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supportEmail">Support Email</Label>
              <Input id="supportEmail" defaultValue="support@dovie.app" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="websiteUrl">Website URL</Label>
              <Input id="websiteUrl" defaultValue="https://dovie.app" />
            </div>
            <div className="flex justify-end">
              <Button>Save Settings</Button>
            </div>
          </CardContent>
        </Card>

        {/* API Keys */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              <CardTitle>API Keys</CardTitle>
            </div>
            <CardDescription>
              Manage integration API keys
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Stripe Secret Key</Label>
                <Badge variant="secondary">Live Mode</Badge>
              </div>
              <div className="flex gap-2">
                <Input type="password" value="sk_live_************************" readOnly />
                <Button variant="outline" size="sm">Update</Button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Clerk Secret Key</Label>
                <Badge variant="secondary">Production</Badge>
              </div>
              <div className="flex gap-2">
                <Input type="password" value="sk_test_************************" readOnly />
                <Button variant="outline" size="sm">Update</Button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Replicate API Token</Label>
                <Badge variant="secondary">Active</Badge>
              </div>
              <div className="flex gap-2">
                <Input type="password" value="r8_****************************" readOnly />
                <Button variant="outline" size="sm">Update</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Team */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <CardTitle>Admin Team</CardTitle>
          </div>
          <CardDescription>
            Manage administrator access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { name: "Admin User", email: "admin@dovie.app", role: "Super Admin", status: "active" },
              { name: "John Manager", email: "john@dovie.app", role: "Admin", status: "active" },
              { name: "Sarah Support", email: "sarah@dovie.app", role: "Support", status: "active" },
            ].map((admin) => (
              <div key={admin.email} className="flex items-center justify-between py-3 border-b last:border-0">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="text-sm bg-secondary">
                      {admin.name.split(" ").map(n => n[0]).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{admin.name}</p>
                    <p className="text-sm text-muted-foreground">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge variant="secondary">{admin.role}</Badge>
                  <Button variant="ghost" size="sm">Edit</Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Button variant="outline">Invite Admin</Button>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            <CardTitle>Security</CardTitle>
          </div>
          <CardDescription>
            Manage account security settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="font-medium">Change Password</p>
              <p className="text-sm text-muted-foreground">
                Update your admin account password
              </p>
            </div>
            <Button variant="outline">Change</Button>
          </div>
          
          <Separator />
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="font-medium">Two-Factor Authentication</p>
              <p className="text-sm text-muted-foreground">
                Add an extra layer of security (Recommended)
              </p>
            </div>
            <Button variant="outline">Enable 2FA</Button>
          </div>
          
          <Separator />
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="font-medium">Login Sessions</p>
              <p className="text-sm text-muted-foreground">
                Manage active login sessions
              </p>
            </div>
            <Button variant="outline">View Sessions</Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <CardTitle>Notifications</CardTitle>
          </div>
          <CardDescription>
            Configure admin notification preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            { title: "System Alerts", description: "Critical system issues and downtime", defaultChecked: true },
            { title: "New User Signups", description: "Daily summary of new registrations", defaultChecked: false },
            { title: "Failed Payments", description: "Subscription payment failures", defaultChecked: true },
            { title: "Video Failures", description: "Video generation failures", defaultChecked: true },
            { title: "Security Alerts", description: "Suspicious activity and login attempts", defaultChecked: true },
          ].map((item) => (
            <div key={item.title} className="flex items-center justify-between">
              <div className="space-y-0.5">
                <p className="font-medium">{item.title}</p>
                <p className="text-sm text-muted-foreground">
                  {item.description}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  defaultChecked={item.defaultChecked}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
